import { memo, useRef, useState, useEffect, SyntheticEvent } from 'react'
import { getAudioPlayerIconsSize } from './utils'
import SvgCircle from 'common-components/SvgCircle'
import Icon from 'common-components/Icon'
import PlayIcon from 'assets/svgr-icons/Play.svg'
import { calculatePercentageOf, playAudioAndCatchAbortError } from 'utils'
import { IProps } from './types'
import * as Styled from './styled'
import { useOnClickOutside } from 'hooks/useOnClickOutside'

const AudioCirclePlayer: React.FC<IProps> = props => {
  const {
    src,
    playIconSize = 12,
    resumeIconSize = 12,
    volume,
    className = '',
    customCircleSize,
    circleStrokeWidth = 10,
    initialStrokeColor,
    progressStrokeColor = 'rgba(79, 220, 111, 1)'
  } = props
  const { circleSize, resetIconSize } = getAudioPlayerIconsSize(
    playIconSize,
    resumeIconSize
  )

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  const [progress, setProgress] = useState(0)

  const onTimeUpdate = (event: SyntheticEvent<HTMLAudioElement>) => {
    if (Number.isNaN(event.currentTarget.duration)) {
      return
    }
    const currentPercent = calculatePercentageOf(
      event.currentTarget.currentTime,
      event.currentTarget.duration
    )
    setProgress(currentPercent)
  }

  const onAudioPlayingEnded = () => {
    setProgress(0)
  }

  const resetPlaying = (ref: HTMLAudioElement | null) => {
    if (ref) {
      ref.pause()
      ref.currentTime = 0
      setProgress(0)
    }
  }

  const startPlaying = () => {
    if (audioRef.current) {
      playAudioAndCatchAbortError(audioRef.current)
    }
  }

  const onPlayOrPauseHandler = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    if (progress) {
      resetPlaying(audioRef.current)
    } else {
      startPlaying()
    }
  }

  useEffect(() => {
    const audio = audioRef.current

    return () => {
      resetPlaying(audio)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  useEffect(() => {
    if (audioRef.current && volume !== undefined) {
      audioRef.current.volume = volume
    }
  }, [volume])

  useOnClickOutside([wrapperRef.current], () => {
    resetPlaying(audioRef.current)
  })

  return (
    <Styled.AudioCirclePlayerWrapper
      ref={wrapperRef}
      onClick={onPlayOrPauseHandler}
      size={customCircleSize || circleSize}
      className={className}
    >
      <SvgCircle
        progress={progress}
        strokeColor={progressStrokeColor}
        backgroundColor={initialStrokeColor}
        strokeWidth={circleStrokeWidth}
        size={customCircleSize || circleSize}
      />

      <audio
        ref={audioRef}
        src={src}
        controls
        style={{ display: 'none' }}
        onTimeUpdate={onTimeUpdate}
        onEnded={onAudioPlayingEnded}
        preload="none"
      ></audio>

      <Styled.AudioCirclePlayerControllers>
        {progress ? (
          <Styled.ResumeIcon size={resetIconSize} />
        ) : (
          <Icon
            icon={<PlayIcon />}
            customSize={playIconSize}
            color="secondary"
          />
        )}
      </Styled.AudioCirclePlayerControllers>
    </Styled.AudioCirclePlayerWrapper>
  )
}

export default memo(AudioCirclePlayer)
